/** Barra superior: inicio, nombre del tablero, colaboradores, compartir y ajustes. */
import type { Presence } from '../../shared/types.ts'
import type { App } from '../app.ts'
import { closePopover, h, openPopoverAt, toolButton } from './dom.ts'
import { icon } from './icons.ts'
import { presentButton } from './present-bar.ts'

export class TopBar {
  readonly el: HTMLElement
  private app: App
  private title: HTMLInputElement
  private presenceEl: HTMLElement
  private statusEl: HTMLElement
  private settingsBtn: HTMLButtonElement
  private presentBtn: HTMLButtonElement & { sync: () => void }
  private commitTimer: number | null = null

  constructor(app: App) {
    this.app = app

    this.title = h('input', {
      class: 'board-title',
      value: app.board.name,
      spellcheck: false,
      'aria-label': 'Nombre del tablero',
      // Se guarda mientras se escribe (con retardo) para que no se pierda
      // el nombre si cierras la pestaña sin salir del campo.
      oninput: () => this.scheduleCommit(),
      onchange: () => this.commitTitle(),
      onblur: () => this.commitTitle(),
      onkeydown: (e: Event) => {
        const ev = e as KeyboardEvent
        if (ev.key === 'Enter') this.title.blur()
        ev.stopPropagation()
      },
    }) as HTMLInputElement

    this.statusEl = h('span', { class: 'status-dot is-wait', title: 'Conectando…' })

    const left = h('div', { class: 'topbar-left surface' }, [
      toolButton({ icon: icon('home'), title: 'Todos los tableros', onClick: () => app.goHome() }),
      this.title,
      this.statusEl,
    ])

    this.presenceEl = h('div', { class: 'presence' })
    this.settingsBtn = toolButton({
      icon: icon('menu'),
      title: 'Más opciones',
      onClick: (e) => this.openSettings(e),
    })

    this.presentBtn = presentButton(app) as HTMLButtonElement & { sync: () => void }

    const right = h('div', { class: 'topbar-right surface' }, [
      this.presenceEl,
      this.presentBtn,
      h(
        'button',
        { class: 'btn-primary', onclick: () => app.openShare() },
        [h('span', { html: icon('share', 18), style: 'display:flex' }), 'Compartir'],
      ),
      this.settingsBtn,
    ])

    this.el = h('div', { class: 'topbar' }, [left, right])
    app.board.onMetaChange(() => this.syncTitle())
  }

  private scheduleCommit(): void {
    if (this.commitTimer !== null) clearTimeout(this.commitTimer)
    this.commitTimer = window.setTimeout(() => {
      this.commitTimer = null
      this.commitTitle()
    }, 600)
  }

  private commitTitle(): void {
    if (this.commitTimer !== null) {
      clearTimeout(this.commitTimer)
      this.commitTimer = null
    }
    const value = this.title.value.trim() || 'Pizarra sin título'
    if (value !== this.app.board.name) {
      this.app.board.setName(value)
      this.app.renameBoard(value)
    }
  }

  syncTitle(): void {
    if (document.activeElement !== this.title) this.title.value = this.app.board.name
  }

  setStatus(status: 'connecting' | 'connected' | 'disconnected'): void {
    this.statusEl.className = `status-dot ${
      status === 'connected' ? '' : status === 'connecting' ? 'is-wait' : 'is-off'
    }`
    this.statusEl.title =
      status === 'connected'
        ? 'Conectado — los cambios se sincronizan en tiempo real'
        : status === 'connecting'
          ? 'Conectando…'
          : 'Sin conexión — tus cambios se guardarán al reconectar'
  }

  /**
   * Los avatares se REUTILIZAN por persona en vez de recrearse: la presencia
   * cambia hasta 25 veces por segundo (cursor, cámara) y, si el botón se
   * reemplazaba entre el mousedown y el mouseup, el clic de "Seguir a X" se
   * perdía justo cuando X estaba activo.
   */
  renderPresence(peers: Presence[], self: Presence): void {
    this.presentBtn.sync()
    const all = [self, ...peers]
    const visibles = all.slice(0, 6)
    const existentes = new Map<string, HTMLButtonElement>()
    for (const child of Array.from(this.presenceEl.children)) {
      const id = (child as HTMLElement).dataset.peerId
      if (id) existentes.set(id, child as HTMLButtonElement)
    }
    const deseados: HTMLElement[] = visibles.map((peer) => {
      const isSelf = peer.id === self.id
      let el = existentes.get(peer.id)
      if (!el) {
        el = h('button', { class: 'avatar', 'data-peer-id': peer.id }) as HTMLButtonElement
        el.addEventListener('click', () => {
          if (el!.dataset.self === '1') return
          this.app.toggleFollow(peer.id)
        })
      }
      el.dataset.self = isSelf ? '1' : ''
      el.className = `avatar ${this.app.store.followingUser === peer.id ? 'is-following' : ''}`
      el.style.background = peer.color
      el.title = isSelf ? `${peer.name} (tú)` : `Seguir a ${peer.name}`
      const texto = initials(peer.name)
      if (el.textContent !== texto) el.textContent = texto
      return el
    })
    if (all.length > 6) {
      const mas = this.presenceEl.querySelector<HTMLElement>('[data-more]') ?? h('span', { class: 'avatar', 'data-more': '1', style: 'background:#616161' })
      mas.textContent = `+${all.length - 6}`
      deseados.push(mas)
    }
    // Solo se toca el DOM si cambió el orden o el conjunto.
    const actuales = Array.from(this.presenceEl.children)
    const igual = actuales.length === deseados.length && actuales.every((c, i) => c === deseados[i])
    if (!igual) this.presenceEl.replaceChildren(...deseados)
  }

  private openSettings(e: MouseEvent): void {
    const anchor = e.currentTarget as HTMLElement
    const { store } = this.app
    const menu = h('div', { class: 'menu-list' }, [
      item('palette', 'Formato del fondo', () => this.app.openBackgroundPicker()),
      item('download', 'Exportar como imagen', () => this.app.exportImage()),
      item('template', 'Plantillas', () => this.app.openTemplates()),
      h('div', { class: 'menu-sep' }),
      toggleItem('users', 'Cursores de colaboradores', store.showCursors, () => {
        store.setPref('showCursors', !store.showCursors)
        this.app.renderer.invalidateOverlay()
      }),
      toggleItem('edit', 'Mejorar formas dibujadas', store.enhanceShapes, () =>
        store.setPref('enhanceShapes', !store.enhanceShapes),
      ),
      toggleItem('grid', 'Ajustar a la cuadrícula', store.snapToGrid, () =>
        store.setPref('snapToGrid', !store.snapToGrid),
      ),
      toggleItem('fit', 'La rueda hace zoom', store.wheelZooms, () => store.setPref('wheelZooms', !store.wheelZooms)),
      toggleItem('hand', 'Un dedo mueve el lienzo', store.touchPans, () =>
        store.setPref('touchPans', !store.touchPans),
      ),
      toggleItem('lock', 'Rechazo de palma: el dedo solo navega', store.palmRejection, () =>
        store.setPref('palmRejection', !store.palmRejection),
      ),
      toggleItem('pen', 'El lápiz siempre escribe en lienzo vacío', store.penAlwaysInks, () =>
        store.setPref('penAlwaysInks', !store.penAlwaysInks),
      ),
      toggleItem('arrowRight', 'Barra de herramientas arriba', store.toolbarTop, () =>
        store.setPref('toolbarTop', !store.toolbarTop),
      ),
      h('div', { class: 'menu-sep' }),
      item('keyboard', 'Atajos de teclado', () => this.app.openShortcuts()),
      item('users', 'Cambiar mi nombre', () => this.app.changeName()),
      item('trash', 'Borrar todo el tablero', () => this.app.clearBoard(), true),
    ])
    openPopoverAt(menu, { anchor, placement: 'bottom', align: 'end' })
  }
}

function item(iconName: string, label: string, onClick: () => void, danger = false): HTMLElement {
  return h(
    'button',
    {
      class: `menu-item ${danger ? 'is-danger' : ''}`,
      onclick: () => {
        closePopover()
        onClick()
      },
    },
    [h('span', { html: icon(iconName, 18), style: 'display:flex;color:currentColor;opacity:.75' }), label],
  )
}

function toggleItem(iconName: string, label: string, on: boolean, onToggle: () => void): HTMLElement {
  const sw = h('span', { class: `switch ${on ? 'is-on' : ''}` })
  return h(
    'button',
    {
      class: 'menu-item',
      onclick: () => {
        onToggle()
        sw.classList.toggle('is-on')
      },
    },
    [h('span', { html: icon(iconName, 18), style: 'display:flex;color:var(--fg-3)' }), label, sw],
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
